import type { RunSubmission } from "./types";

const STORAGE_KEY = "snake-arena-run-queue-v1";
/** Hard cap on queued runs - a long offline stretch must never grow this without bound. When full, the oldest queued run is dropped to make room for the newest (analytics data, not a leaderboard record - losing the oldest stale entry is preferable to losing the player's most recent attempt). */
const MAX_QUEUE_SIZE = 30;

/**
 * Upgrades a run queued by a pre-WORMHOLE/STARFRUIT build (missing the 4
 * new fields entirely) to the current shape, in place in memory - the
 * stored JSON itself is only rewritten on the next `writeQueue()` call
 * (e.g. after a flush), never deleted or dropped just for being old-shaped.
 * Missing counters default to 0; a missing `completionReason` on an old
 * completed run (which could only ever have meant reaching the real
 * target) infers `"length_target"`; a failed run never gets one.
 */
/** A queued run stored by a pre-Scoring-V2 build is missing every new field entirely - normalized the same way WORMHOLE/STARFRUIT's fields already are: `scoringVersion` defaults to 1 (Bite Score only, exactly what that old queued run always meant), `biteScore` defaults to its own `score`, and every other Scoring V2 counter defaults to 0. */
function normalizeRunSubmission(raw: RunSubmission): RunSubmission {
  const scoringVersion = typeof raw.scoringVersion === "number" ? raw.scoringVersion : 1;
  return {
    ...raw,
    wormholeCount: typeof raw.wormholeCount === "number" ? raw.wormholeCount : 0,
    starfruitSpawnedCount: typeof raw.starfruitSpawnedCount === "number" ? raw.starfruitSpawnedCount : 0,
    starfruitEatenCount: typeof raw.starfruitEatenCount === "number" ? raw.starfruitEatenCount : 0,
    completionReason:
      raw.completionReason !== undefined ? raw.completionReason : raw.status === "completed" ? "length_target" : null,
    scoringVersion,
    biteScore: typeof raw.biteScore === "number" ? raw.biteScore : raw.score,
    skillScore: typeof raw.skillScore === "number" ? raw.skillScore : 0,
    paceBonus: typeof raw.paceBonus === "number" ? raw.paceBonus : 0,
    moveCount: typeof raw.moveCount === "number" ? raw.moveCount : 0,
    perfectFoldCount: typeof raw.perfectFoldCount === "number" ? raw.perfectFoldCount : 0,
    lastSecondTurnCount: typeof raw.lastSecondTurnCount === "number" ? raw.lastSecondTurnCount : 0,
    tailGrazeCount: typeof raw.tailGrazeCount === "number" ? raw.tailGrazeCount : 0,
    threadTheNeedleCount: typeof raw.threadTheNeedleCount === "number" ? raw.threadTheNeedleCount : 0,
    doubleDangerCount: typeof raw.doubleDangerCount === "number" ? raw.doubleDangerCount : 0,
    bestFlowStreak: typeof raw.bestFlowStreak === "number" ? raw.bestFlowStreak : 0,
    pauseCount: typeof raw.pauseCount === "number" ? raw.pauseCount : 0,
    pausedDurationMs: typeof raw.pausedDurationMs === "number" ? raw.pausedDurationMs : 0,
    trickBiteCount: typeof raw.trickBiteCount === "number" ? raw.trickBiteCount : 0,
    clutchBiteCount: typeof raw.clutchBiteCount === "number" ? raw.clutchBiteCount : 0,
  };
}

function readQueue(): RunSubmission[] {
  try {
    const raw = globalThis.localStorage?.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.map(normalizeRunSubmission) : [];
  } catch {
    return [];
  }
}

function writeQueue(queue: RunSubmission[]): void {
  try {
    globalThis.localStorage?.setItem(STORAGE_KEY, JSON.stringify(queue));
  } catch {
    // Ignore storage failures - the run itself already happened and local
    // progress was already saved via ProgressStorage; losing the queued
    // copy only means this one run's stats/score never reach the server.
  }
}

/** Same level, scoring population and platform - the key `level_scores` keeps one best per (PR34: `platform` is part of it, so a Mobile and a Desktop capture never replace each other). */
function sameLeaderboardSlot(a: RunSubmission, b: RunSubmission): boolean {
  return a.levelId === b.levelId && a.scoringVersion === b.scoringVersion && a.platform === b.platform;
}

/** `candidate` would replace `current` as the server-side best (`record_level_score()`'s V2 order: score desc, then active duration asc). */
function ranksHigher(candidate: RunSubmission, current: RunSubmission): boolean {
  if (candidate.score !== current.score) return candidate.score > current.score;
  return candidate.activeDurationMs < current.activeDurationMs;
}

/** What a flush attempt decides about one queued run, so the queue never has to guess whether a failure was transient. */
export type FlushOutcome = "sent" | "retry-later" | "drop";

/**
 * Local, size-bounded queue of run submissions that failed to reach the
 * server (offline, or a transient error), so they aren't lost. Never
 * touches `ProgressStorage` - local high scores/stars are already saved
 * the instant a level completes, independent of this queue entirely.
 * `run_id` (already assigned by `RunTracker.buildSubmission`) makes every
 * retry idempotent server-side, so flushing the same entry twice (e.g. a
 * flush that "succeeded" but whose response was lost) is always safe.
 */
export const RunQueue = {
  list(): readonly RunSubmission[] {
    return readQueue();
  },

  size(): number {
    return readQueue().length;
  },

  enqueue(run: RunSubmission): void {
    const queue = readQueue();
    if (queue.some((entry) => entry.runId === run.runId)) return;
    queue.push(run);
    while (queue.length > MAX_QUEUE_SIZE) queue.shift();
    writeQueue(queue);
  },

  /**
   * PR34: local-only capture of a COMPLETED run while Online Features are
   * off (never asked yet, or PLAY OFFLINE). Nothing is ever sent from here -
   * `flush()` is only ever reached through an opted-in service - so this
   * keeps the consent boundary intact while making sure a leaderboard-worthy
   * result isn't simply thrown away before the player opts in.
   *
   * Keeps at most ONE captured completion per leaderboard slot (see
   * `sameLeaderboardSlot()`): a new capture only replaces an already-queued
   * completion for that slot when it would actually rank higher on the
   * server (`record_level_score()`'s own V2 order: score desc, then
   * duration asc), and is skipped otherwise. That bounds pre-opt-in
   * captures by the number of levels (25) rather than by how often a
   * player replays, so the existing `MAX_QUEUE_SIZE` FIFO cap can never
   * evict a player's best result in favor of later, worse replays. Failed
   * runs are never captured here - they're gameplay telemetry, not
   * leaderboard results, and only ever leave the device after opt-in.
   */
  captureWhileOffline(run: RunSubmission): void {
    if (run.status !== "completed") return;
    const queue = readQueue();
    if (queue.some((entry) => entry.runId === run.runId)) return;
    const rivals = queue.filter((entry) => entry.status === "completed" && sameLeaderboardSlot(entry, run));
    if (rivals.some((entry) => !ranksHigher(run, entry))) return;
    const rivalIds = new Set(rivals.map((entry) => entry.runId));
    const next = queue.filter((entry) => !rivalIds.has(entry.runId));
    next.push(run);
    while (next.length > MAX_QUEUE_SIZE) next.shift();
    writeQueue(next);
  },

  /**
   * Attempts to send every queued run, in order (oldest first). Stops at
   * the first `"retry-later"` outcome (server/network still down - no
   * point hammering the rest right now) but still applies every `"sent"`/
   * `"drop"` decision made before that point. Returns each attempted run's
   * outcome, keyed by `runId`, so a caller can tell whether *its specific*
   * run actually made it (a run never reached this call - because
   * `flush()` stopped before getting to it - simply has no entry in the
   * returned map).
   */
  async flush(submit: (run: RunSubmission) => Promise<FlushOutcome>): Promise<Map<string, FlushOutcome>> {
    const queue = readQueue();
    const outcomes = new Map<string, FlushOutcome>();
    if (queue.length === 0) return outcomes;

    for (const run of queue) {
      const outcome = await submit(run);
      outcomes.set(run.runId, outcome);
      if (outcome === "retry-later") break;
      // "sent" and "drop" both remove the entry - only the reason differs.
    }
    // PR34: re-read before writing and remove only what THIS flush decided
    // ("sent"/"drop"), instead of overwriting the whole queue with this
    // flush's own snapshot - a run enqueued (or captured) while this flush
    // was awaiting the network must never be silently erased by it.
    const decided = new Set([...outcomes].filter(([, outcome]) => outcome !== "retry-later").map(([runId]) => runId));
    writeQueue(readQueue().filter((run) => !decided.has(run.runId)));
    return outcomes;
  },
};
